"use client";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

// hooks form
import { FormProvider, useForm, useFormContext, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

// ui components
import Input from "@/components/ui/input/Input";
import Checkbox from "@/components/ui/input/Checkbox";
import Spinner from "@/components/ui/Spinner";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";

const FormStatusContext = createContext(null);

export const useFormStatus = () => useContext(FormStatusContext);

const DynamicForm = ({
  schema,
  endpoint,
  onSuccess,
  onSubmit,
  onError,
  showRecaptcha = false,
  defaultValues = {},
  resetOnSuccess = false,
  successMessage = "",
  customErrorMessage = "",
  dryRun = false,
  getCaptchaToken,
  className = "",
  children,
  mode = "all", // all, onBlur, onChange, onSubmit
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const methods = useForm({
    mode: mode,
    resolver: schema ? zodResolver(schema) : undefined,
    defaultValues,
  });

  const { handleSubmit, reset, clearErrors } = methods;

  // defaultValues değiştiğinde formu senkronize et
  useEffect(() => {
    if (defaultValues && typeof defaultValues === "object") {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  const handleFormSubmit = useCallback(
    async (data) => {
      // Önceki mesajları temizle
      setError("");
      setSuccess("");

      if (onSubmit) {
        onSubmit(data);
      }

      // Dry run modu: API çağrısı yapmadan sadece veriyi döndür
      if (dryRun) {
        onSuccess?.(data);
        if (successMessage) setSuccess(successMessage);
        return;
      }

      // Endpoint kontrolü
      if (!endpoint) {
        const errorMsg = "DynamicForm bileşeni için endpoint gerekli, ancak sağlanmadı.";
        console.error(errorMsg);
        setError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      setSubmitting(true);

      try {
        // Headers oluştur
        const headers = new Headers();
        headers.set("Content-Type", "application/json");

        // reCAPTCHA token'ı ekle
        if (showRecaptcha && typeof getCaptchaToken === "function") {
          try {
            const token = await getCaptchaToken("DynamicForm");
            if (token) {
              headers.append("CaptchaToken", token);
            }
          } catch (captchaError) {
            console.warn("Captcha token alınamadı:", captchaError);
            // Token alınamazsa devam et
          }
        }

        // API isteği
        const response = await fetch(endpoint, {
          method: "POST",
          body: JSON.stringify(data),
          headers,
        });

        // Response'u parse et
        let result = null;
        const contentType = response.headers.get("content-type");
        
        if (contentType && contentType.includes("application/json")) {
          try {
            result = await response.json();
          } catch (parseError) {
            console.error("JSON parse hatası:", parseError);
          }
        }

        // Başarılı response kontrolü
        if (response.ok) {
          // API'den gelen success flag kontrolü (varsa)
          if (result && typeof result.isSuccess !== "undefined") {
            if (result.isSuccess) {
              handleSuccess(result.data || data);
            } else {
              handleError(result);
            }
          } else {
            // isSuccess field yoksa response.ok yeterli
            handleSuccess(result || data);
          }
        } else {
          // HTTP hata durumu
          handleError(result, response.status);
        }
      } catch (fetchError) {
        // Network veya diğer hatalar
        console.error("Form submit hatası:", fetchError);
        const errorMsg = fetchError instanceof Error 
          ? fetchError.message 
          : customErrorMessage || "Bir hata oluştu. Lütfen tekrar deneyin.";
        setError(errorMsg);
        onError?.(errorMsg);
      } finally {
        setSubmitting(false);
      }
    },
    [endpoint, getCaptchaToken, showRecaptcha, customErrorMessage, dryRun, onSuccess, onError]
  );

  // Başarı durumunu yönet
  const handleSuccess = useCallback(
    (data) => {
      onSuccess?.(data);
      if (successMessage) {
        setSuccess(successMessage);
      }
      if (resetOnSuccess) {
        reset();
        // clearErrors'u bir sonraki tick'te çağır
        setTimeout(() => clearErrors(), 0);
      }
    },
    [onSuccess, successMessage, resetOnSuccess, reset, clearErrors]
  );

  // Hata durumunu yönet
  const handleError = useCallback(
    (result, statusCode) => {
      let errorMsg = customErrorMessage || "Bir hata oluştu. Lütfen tekrar deneyin.";

      if (result) {
        // Farklı hata mesajı formatlarını kontrol et
        errorMsg =
          result.error?.message ||
          result.errorMessage ||
          result.message ||
          (statusCode === 404 ? "İstek yapılan kaynak bulunamadı." :
           statusCode === 403 ? "Bu işlem için yetkiniz bulunmamaktadır." :
           statusCode === 401 ? "Oturum süreniz dolmuş. Lütfen tekrar giriş yapın." :
           customErrorMessage || "Bir hata oluştu. Lütfen tekrar deneyin.");
      }

      setError(errorMsg);
      onError?.(errorMsg);
    },
    [customErrorMessage, onError]
  );

  const statusValue = useMemo(
    () => ({ submitting, error, setError, success, setSuccess }),
    [submitting, error, success]
  );

  return (
    <FormStatusContext.Provider value={statusValue}>
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(handleFormSubmit)} className={className}>
          {children}
        </form>
      </FormProvider>
    </FormStatusContext.Provider>
  );
};

const Field = ({
  name,
  rules,
  component,
  type = "text",
  options,
  render,
  ...rest
}) => {
  const { formState } = useFormContext();
  const fieldError = formState?.errors?.[name]?.message;

  return (
    <Controller
      name={name}
      rules={rules}
      render={({ field }) => {
        // Custom render fonksiyonu varsa kullan
        if (typeof render === "function") {
          return render({ field, error: fieldError });
        }

        // Custom component varsa kullan
        if (component) {
          const Comp = component;
          return <Comp id={name} error={fieldError} {...rest} {...field} />;
        }

        // Checkbox
        if (type === "checkbox") {
          return (
            <Checkbox
              id={name}
              checked={!!field.value}
              onChange={(e) => field.onChange(e.target.checked)}
              error={fieldError}
              {...rest}
            >
              {rest.label}
            </Checkbox>
          );
        }

        // Radio
        if (type === "radio") {
          return (
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  id={name}
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="cursor-pointer"
                />
                <span>{rest.label}</span>
              </label>
              {fieldError && (
                <p className="pl-1 text-xs text-danger">{String(fieldError)}</p>
              )}
            </div>
          );
        }

        // Select
        if (type === "select") {
          return (
            <div className="flex flex-col gap-2">
              {rest.label && (
                <label htmlFor={name} className="text-sm text-foreground-muted">
                  {rest.label}
                </label>
              )}
              <select
                id={name}
                value={field.value ?? ""}
                onChange={(e) => field.onChange(e.target.value)}
                className="w-full rounded-md border p-4 bg-white"
              >
                {!field.value && (
                  <option value="">Seçiniz...</option>
                )}
                {(options || []).map((opt) => (
                  <option key={String(opt.value)} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {fieldError && (
                <p className="pl-1 text-xs text-danger">{String(fieldError)}</p>
              )}
            </div>
          );
        }

        // Default: text input
        return (
          <Input
            id={name}
            error={fieldError}
            type={type}
            {...rest}
            {...field}
            value={field.value ?? ""}
          />
        );
      }}
    />
  );
};

const SubmitButton = ({ children, className = "", spanClassName = "", disabled = false }) => {
  const { submitting } = useFormStatus() || {};
  const isDisabled = disabled || submitting;

  return (
    <button 
      type="submit" 
      className={`relative ${className}`} 
      disabled={isDisabled}
    >
      <span className={clsx(spanClassName, submitting ? "opacity-0" : "opacity-100")}>
        {children}
      </span>
      {submitting && (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <Spinner className="h-4 w-4" />
        </span>
      )}
    </button>
  );
};

const ErrorInfo = ({ className = "", showClose = false, customErrorMessage = null }) => {
  const { error, setError } = useFormStatus() || {};
  if (!error) return null;

  return (
    <div
      className={`flex items-start gap-[10px] rounded-[10px] border border-[#FECACA] bg-[#FEF2F2] p-4 ${className}`}
      role="alert"
      style={{ alignSelf: "stretch" }}
    >
      <FontAwesomeIcon
        icon={byPrefixAndName.far["circle-exclamation"]}
        className="mt-[2px] text-red-600"
      />
      <div className="flex-1">
        <span className="text-[16px] text-[#1D1E20]">{customErrorMessage || error}</span>
      </div>
      {showClose && (
        <button 
          type="button" 
          onClick={() => setError("")} 
          className="ml-2 text-lg leading-none hover:opacity-70"
          aria-label="Hatayı kapat"
        >
          ×
        </button>
      )}
    </div>
  );
};

const SuccessInfo = ({ className = "" }) => {
  const { success, setSuccess } = useFormStatus() || {};
  if (!success) return null;

  return (
    <div className={`flex items-center justify-between ${className}`} role="status">
      <span>{success}</span>
      <button 
        type="button" 
        onClick={() => setSuccess("")} 
        className="ml-2 text-lg leading-none hover:opacity-70"
        aria-label="Mesajı kapat"
      >
        ×
      </button>
    </div>
  );
};

DynamicForm.Field = Field;
DynamicForm.Button = SubmitButton;
DynamicForm.Error = ErrorInfo;
DynamicForm.Success = SuccessInfo;

export default DynamicForm;